import React, { useState, useId, cloneElement } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { getUTMs } from "../../hooks/useUTMs";
import ConsentCheckboxes from "./ConsentCheckboxes";
import { Loader2, Send, CheckCircle2, BookOpen, ArrowRight, Download } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HOTMART_URL = "https://hotmart.com/pt-br/marketplace/produtos/o-codigo-da-riqueza-com-consorcio/G105260199T";

export default function ContactForm() {
  const [f, setF] = useState({ name: "", phone: "", email: "", objective: "", horizon: "" });
  const [ok, setOk] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lgpdAccepted, setLgpdAccepted] = useState(false);
  const [marketingAccepted, setMarketingAccepted] = useState(false);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  // Captura UTMs do sessionStorage para propagar ao Hotmart
  const getHotmartUrl = () => {
    try {
      const utms = JSON.parse(sessionStorage.getItem("utms") || "{}");
      const params = new URLSearchParams(utms).toString();
      return params ? `${HOTMART_URL}&${params}` : HOTMART_URL;
    } catch {
      return HOTMART_URL;
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!lgpdAccepted) {
      toast.error("Marque a autorização de contato para enviar sua solicitação.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads/v2`, { ...f, lgpd_accepted: true, source: "landing_patrimonio_v1", priority: "none", consents: { privacy: lgpdAccepted, marketing: marketingAccepted }, utm: getUTMs() });
      setOk(true);
      toast.success("Solicitação enviada com sucesso.");
      // Dispara evento GA4 + Meta Pixel
      if (window.gtag) window.gtag("event", "Lead", { event_category: "form", event_label: f.objective });
      if (window.fbq) window.fbq("track", "Lead", { value: 0, currency: "BRL" });
    } catch (err) {
      console.error(err);
      let msg = "Erro ao enviar. Tente o WhatsApp abaixo.";
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0];
        if (first.loc?.includes("email")) {
          msg = "Por favor, insira um e-mail válido (ex: seu@email.com).";
        } else if (first.loc?.includes("phone")) {
          msg = "Por favor, insira um WhatsApp válido com DDD (mínimo 6 dígitos).";
        } else if (first.msg) {
          msg = first.msg;
        }
      } else if (typeof detail === "string") {
        msg = detail;
      }
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <section id="contato" className="py-24 lg:py-32 relative" data-testid="contact-form">
      <div className="container max-w-5xl">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12 text-center">
          <div className="eyebrow mb-4 justify-center"><span className="kicker-line"/>Diagnóstico patrimonial</div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-light">Vamos tomar um café virtual e falar sobre <span className="italic text-gold-gradient">o seu patrimônio</span>.</h2>
          <p className="text-muted-foreground max-w-xl mx-auto mt-6">Preencha os dados abaixo e receba uma análise personalizada. Sem compromisso, sem esteira, sem venda.</p>
          <div className="glow-line w-24 mx-auto mt-8"/>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }}>
          {ok ? (
            <div className="bg-card-gradient border border-primary/40 shadow-gold rounded-sm p-12 text-center" data-testid="form-success">
              <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center bg-primary/10 ring-gold">
                <CheckCircle2 className="text-primary" size={28}/>
              </div>
              <h3 className="font-display text-3xl mb-3">Solicitação recebida.</h3>
              <p className="text-muted-foreground max-w-md mx-auto mb-10">
                Nosso escritório entrará em contato em até 48h úteis. Seus dados ficam protegidos — nunca são compartilhados.
              </p>

              {/* Ebook Gratuito como Cortesia */}
              <div className="border-t border-primary/20 pt-10 mt-4">
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-mono tracking-widest px-4 py-1.5 rounded-full mb-5">
                  <BookOpen size={12}/> CORTESIA EXCLUSIVA
                </div>
                <h4 className="font-display text-2xl md:text-3xl font-light mb-3">
                  Enquanto aguarda nosso contato, acesse<br/>
                  <span className="italic text-gold-gradient">"O Código da Riqueza com Consórcio"</span>
                </h4>
                <p className="text-muted-foreground max-w-sm mx-auto mb-8 text-sm leading-relaxed">
                  Como agradecimento pelo seu cadastro, liberamos o seu exemplar digital gratuito sobre estratégias de alavancagem patrimonial.
                </p>
                <a
                  href="/downloads/O_Codigo_da_Riqueza.pdf"
                  download="O_Codigo_da_Riqueza_com_Consorcio.pdf"
                  data-testid="ebook-free-download-cta"
                  className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-medium text-base transition-all hover:translate-y-[-2px] shadow-gold"
                  style={{ background: "var(--gold-gradient)", color: "#0f0d0a" }}
                >
                  Baixar Ebook Gratuito (PDF) <Download size={16}/>
                </a>
                <p className="text-xs text-muted-foreground mt-4">Download direto e seguro em formato PDF.</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-card-gradient border border-primary/25 shadow-gold rounded-sm p-8 md:p-12 relative">
              <div aria-hidden="true" className="hidden sm:block absolute top-4 right-4 font-mono text-[10px] tracking-[0.25em] text-muted-foreground">FORMULÁRIO · CONFIDENCIAL</div>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <Field label="Nome completo" required><input data-testid="form-name" required maxLength={100} value={f.name} onChange={set("name")} className={input} placeholder="Seu nome"/></Field>
                <Field label="WhatsApp" required><input data-testid="form-phone" required maxLength={20} type="tel" value={f.phone} onChange={set("phone")} className={input} placeholder="(00) 00000-0000"/></Field>
              </div>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <Field label="E-mail" required><input data-testid="form-email" required type="email" maxLength={255} value={f.email} onChange={set("email")} className={input} placeholder="seu@email.com"/></Field>
                <Field label="Qual seu objetivo principal?" required>
                  <select data-testid="form-objective" required value={f.objective} onChange={set("objective")} className={input}>
                    <option value="">Selecione</option>
                    <option value="moradia">Moradia</option>
                    <option value="renda">Renda</option>
                    <option value="aposentadoria">Aposentadoria</option>
                    <option value="diversificacao">Diversificação</option>
                    <option value="outro">Outro</option>
                  </select>
                </Field>
              </div>
              <Field label="Qual o seu horizonte de decisão?" required>
                <select data-testid="form-horizon" required value={f.horizon} onChange={set("horizon")} className={input}>
                  <option value="">Selecione</option>
                  <option value="imediato">Imediato (0 a 6 meses)</option>
                  <option value="curto">Curto prazo (6 meses a 2 anos)</option>
                  <option value="medio">Médio prazo (2 a 5 anos)</option>
                  <option value="longo">Longo prazo (5+ anos)</option>
                </select>
              </Field>
              
              <div className="mt-6">
                <ConsentCheckboxes
                  idPrefix="contato"
                  contact={lgpdAccepted}
                  onContactChange={setLgpdAccepted}
                  marketing={marketingAccepted}
                  onMarketingChange={setMarketingAccepted}
                />
              </div>

              <button type="submit" disabled={loading} data-testid="form-submit" className="mt-6 w-full py-4 rounded-full font-medium text-primary-foreground text-base transition-all hover:translate-y-[-1px] disabled:opacity-70 flex items-center justify-center gap-2 shadow-gold" style={{ background: "var(--gold-gradient)" }}>
                {loading ? (<><Loader2 className="animate-spin" size={18}/>Enviando...</>) : (<>Receber meu diagnóstico <Send size={16}/></>)}
              </button>
                          </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}

const input = "w-full px-4 py-3.5 rounded-sm bg-background border border-input text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/40 transition-all font-body";

function Field({ label, required, children }) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium mb-2 text-muted-foreground">
        {label}{required && <span className="text-primary ml-1" aria-hidden="true">*</span>}
      </label>
      {cloneElement(children, { id })}
    </div>
  );
}
